import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Stack, Typography } from '@mui/material'
import type { FC, PropsWithChildren, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { type To } from 'react-router'

import { COLORS, ICONS, rotateYCss, unit } from '@hcr/ui'
import { isNotUndefined } from '@hcr/utils'

import { SvgLogoHolidayClub } from '../../../../../assets'
import { LayoutBase, Metadata } from '../../../../common'
import type { AdditionalInfoListItem } from '../additional-info-list'
import { AdditionalInfoList } from '../additional-info-list'

import {
  LayoutTicketDetailsActiveContainer,
  LayoutTicketDetailsActiveContent,
  LayoutTicketDetailsActiveHeader,
  LayoutTicketDetailsActiveHeaderContent,
  LayoutTicketDetailsActiveArrowLinkIcon,
  LayoutTicketDetailsActiveInfo,
  LayoutTicketDetailsActiveInfoContent,
  LayoutTicketDetailsActiveName,
  LayoutTicketDetailsActiveInfoContentSection,
  LayoutTicketDetailsActiveFooter,
} from './LayoutTicketDetailsActiveComponents'

interface LayoutTicketDetailsActiveProps extends PropsWithChildren {
  additionalInfo: AdditionalInfoListItem[]
  backNavigationPath: To
  cta: ReactNode
  destinationName: string
  guests?: string
  name: ReactNode
  validity: string
}

export const LayoutTicketDetailsActive: FC<LayoutTicketDetailsActiveProps> = ({
  additionalInfo,
  backNavigationPath,
  children,
  cta,
  destinationName,
  guests,
  name,
  validity,
}) => {
  const { t } = useTranslation()

  return (
    <LayoutBase>
      <Metadata title={t('page-title.ticket-details')} />
      <LayoutTicketDetailsActiveContainer>
        <LayoutTicketDetailsActiveContent>
          <LayoutTicketDetailsActiveHeader as='header'>
            <LayoutTicketDetailsActiveHeaderContent>
              <LayoutTicketDetailsActiveArrowLinkIcon to={backNavigationPath} aria-label={t('tickets.back-to-booking')}>
                <FontAwesomeIcon icon={ICONS.farArrowLeft} />
              </LayoutTicketDetailsActiveArrowLinkIcon>
              <Stack alignItems='center' css={rotateYCss}>
                <Typography component='h1' variant='headlineL' color={COLORS.white}>
                  {destinationName}
                </Typography>
                <SvgLogoHolidayClub height={unit(2)} fill={COLORS.white} />
              </Stack>
            </LayoutTicketDetailsActiveHeaderContent>
          </LayoutTicketDetailsActiveHeader>
          <LayoutTicketDetailsActiveInfo as='main'>
            <LayoutTicketDetailsActiveInfoContent>
              <LayoutTicketDetailsActiveName>
                <Typography variant='headlineM'>{name}</Typography>
              </LayoutTicketDetailsActiveName>
              <LayoutTicketDetailsActiveInfoContentSection>
                {isNotUndefined(guests) && <Typography variant='titleS'>{guests}</Typography>}
                <Typography variant='bodyM' textAlign='center'>
                  {validity}
                </Typography>
              </LayoutTicketDetailsActiveInfoContentSection>
              {children}
            </LayoutTicketDetailsActiveInfoContent>
          </LayoutTicketDetailsActiveInfo>
        </LayoutTicketDetailsActiveContent>
        <LayoutTicketDetailsActiveFooter as='footer'>
          <AdditionalInfoList items={additionalInfo} />
          {cta}
        </LayoutTicketDetailsActiveFooter>
      </LayoutTicketDetailsActiveContainer>
    </LayoutBase>
  )
}
